import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { fetchOutgoingTransactionById } from '@/api/transactions';
import { OutgoingTransactionDetailHeader } from '@/app/(client)/transactions/-components/OutgoingTransactionDetailHeader';
import {
  outgoingTransactionDetailKeys,
  useOutgoingTransactionDetail,
} from '@/app/(client)/transactions/-hooks/useOutgoingTransactionDetail';
import type { TabEntry } from '@/components/shared/PageTabs';
import PageTabs from '@/components/shared/PageTabs';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/(client)/transactions/(pages)/(detail)/outgoing/$txId',
)({
  beforeLoad: ({ params, location }) => {
    const bare = `/transactions/outgoing/${params.txId}`;
    if (location.pathname === bare || location.pathname === `${bare}/`) {
      throw redirect({
        to: '/transactions/outgoing/$txId/overview',
        params: { txId: params.txId },
      });
    }
  },
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: outgoingTransactionDetailKeys.detail(params.txId),
      queryFn: () => fetchOutgoingTransactionById(params.txId),
    }),
  component: OutgoingTransactionDetailLayout,
});

function OutgoingTransactionDetailLayout() {
  const { txId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'fiberTransactions' });
  const { data: transaction } = useOutgoingTransactionDetail(txId);

  const base = `/transactions/outgoing/${txId}`;
  const tabs: TabEntry[] = [
    { key: 'overview', label: t('detail.tabOverview'), to: `${base}/overview` },
    { key: 'tracing', label: t('detail.tabTracing'), to: `${base}/tracing` },
  ];

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-y-auto'>
      {transaction ? (
        <OutgoingTransactionDetailHeader transaction={transaction} />
      ) : (
        <div className='h-32 border-b border-border bg-muted/20 animate-pulse' />
      )}
      <div className='sticky top-0 z-20 bg-background'>
        <PageTabs tabs={tabs} mode='path' moreLabel={t('detail.more')} />
      </div>
      <div className='@container flex flex-col flex-1 min-h-[70dvh] p-4 sm:p-6 w-full'>
        <Outlet />
      </div>
    </div>
  );
}
